import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import {
  adminFeatureFlags, adminSaveFeatureFlags,
  adminOverview,
  adminUsers,
  adminSuspendUser,
  adminToggleAdmin,
  adminCreateUser,
  adminDeleteUser,
  adminResetPassword,
  adminCompanies,
  adminVerifyCompany,
  adminCreateCompany,
  adminUpdateCompany,
  adminDeleteCompany,
  adminAgencies,
  adminCreateAgency,
  adminUpdateAgency,
  adminDeleteAgency,
  adminColleges,
  adminCreateCollege,
  adminUpdateCollege,
  adminDeleteCollege,
  adminModuleCatalog,
  adminCollegeTeam,
  adminCollegeTeamInvite,
  adminCollegeTeamRemove,
  adminPricing,
  adminStudentPrices,
  adminSaveStudentPrices,
  adminUpdatePlan,
  adminUpdateCreditPacks,
  adminWaitlist,
  adminJobs,
  adminLeads,
  adminContacts,
} from "@/lib/api";
import {
  Users as UsersIcon,
  Building2,
  Briefcase,
  GraduationCap,
  Linkedin,
  Mail,
  ListChecks,
  ShieldCheck,
  TrendingUp,
  Loader2,
  Search,
  Check,
  X,
  ExternalLink,
  UserPlus,
  Trash2,
  KeyRound,
  SlidersHorizontal,
} from "lucide-react";

/* ---------------- Overview ---------------- */
export function AdminOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    adminOverview()
      .then(({ data }) => mounted && setData(data))
      .catch((e) => mounted && setErr(e?.response?.data?.detail || "Failed to load"));
    return () => { mounted = false; };
  }, []);

  if (err) return <Empty msg={err} />;
  if (!data) return <SkeletonGrid />;

  const stats = [
    { label: "Total users", value: data.users.total, sub: `+${data.users.new_last_7d} last 7d`, icon: UsersIcon, color: "#C2410C" },
    { label: "Candidates", value: data.users.candidates, sub: `${data.users.company_users} company users`, icon: UsersIcon, color: "#15803D" },
    { label: "Companies", value: data.companies, sub: `${data.jobs.open} open jobs`, icon: Building2, color: "#D97706" },
    { label: "Applications", value: data.applications, sub: `${data.interviews} interviews scored`, icon: TrendingUp, color: "#C2410C" },
    { label: "Waitlist", value: data.waitlist.total, sub: `${data.waitlist.candidates} cand · ${data.waitlist.companies} co.`, icon: ListChecks, color: "#15803D" },
    { label: "Resumes uploaded", value: data.resumes, sub: "Lifetime", icon: Briefcase, color: "#D97706" },
    { label: "LinkedIn audits", value: data.linkedin_leads, sub: "Public lead magnet", icon: Linkedin, color: "#0a66c2" },
    { label: "Admins", value: data.users.admins, sub: `${data.users.suspended} suspended`, icon: ShieldCheck, color: "#7c3aed" },
  ];

  return (
    <div data-testid="admin-overview">
      <Header title="Overview" subtitle="Platform pulse at a glance." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl border border-stone-800 bg-[#1c1917] p-5 hover:border-stone-700 transition-colors"
            data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-widest text-stone-400">{s.label}</span>
              <s.icon size={16} style={{ color: s.color }} />
            </div>
            <div className="mt-3 font-display text-4xl">{s.value}</div>
            <p className="mt-1 text-xs text-stone-400">{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Users ---------------- */
export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [tick, setTick] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await adminUsers(q, roleFilter);
        if (alive) setUsers(data);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [q, roleFilter, tick]);

  const refresh = () => setTick((t) => t + 1);

  const toggleSuspend = (u) => {
    adminSuspendUser(u.user_id, !u.suspended).then(refresh);
  };
  const toggleAdmin = (u) => {
    adminToggleAdmin(u.user_id, !u.is_admin).then(refresh);
  };
  const deleteUser = async (u) => {
    if (!(await confirm({ title: "Please confirm", description: `Delete ${u.name || u.email}? This removes their account and data and cannot be undone.`, danger: true }))) return;
    setBusyId(u.user_id);
    try {
      await adminDeleteUser(u.user_id);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user.");
    } finally {
      setBusyId(null);
    }
  };
  const resetPassword = async (u) => {
    const pw = (await confirm({ title: "Set a new password", description: `Set a new password for ${u.name || u.email} (min 8 characters):`, prompt: { type: "password", minLength: 8, placeholder: "New password (min 8 chars)" }, confirmText: "Save" }));
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusyId(u.user_id);
    try {
      await adminResetPassword(u.user_id, pw);
      toast.success("Password reset. The user has been signed out of active sessions.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Header title="Users" subtitle="Everyone with a Wishire account." />
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="admin-add-user"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] transition-colors shrink-0"
        >
          <UserPlus size={16} /> Add user
        </button>
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email"
            data-testid="admin-users-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-full bg-[#1c1917] border border-stone-800 text-sm placeholder:text-stone-500 focus:outline-none focus:border-stone-600"
          />
        </div>
        <div className="flex items-center gap-2">
          {["", "candidate", "college", "company", "agency", "admin"].map((r) => (
            <button
              key={r || "all"}
              type="button"
              onClick={() => setRoleFilter(r)}
              data-testid={`admin-users-filter-${r || "all"}`}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                roleFilter === r
                  ? "bg-[#C2410C] border-[#C2410C] text-white"
                  : "border-stone-700 text-stone-300 hover:bg-stone-800/60"
              }`}
            >
              {r || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-stone-400">
            <Loader2 className="animate-spin inline" size={20} />
          </div>
        ) : users.length === 0 ? (
          <Empty msg="No users match." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-900/60 text-xs font-mono uppercase tracking-widest text-stone-400">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>LinkedIn</Th>
                  <Th>Score</Th>
                  <Th>Joined</Th>
                  <Th>Last login</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-stone-800/80 hover:bg-stone-900/40">
                    <Td>
                      <div className="flex items-center gap-2">
                        {u.is_admin && (
                          <span className="text-[10px] font-mono uppercase bg-[#7c3aed] text-white px-1.5 py-0.5 rounded">
                            ADMIN
                          </span>
                        )}
                        {u.suspended && (
                          <span className="text-[10px] font-mono uppercase bg-red-600 text-white px-1.5 py-0.5 rounded">
                            SUSP
                          </span>
                        )}
                        <span>{u.name || "—"}</span>
                      </div>
                    </Td>
                    <Td><span className="text-stone-300">{u.email}</span></Td>
                    <Td>
                      <span className="text-xs font-mono uppercase tracking-widest text-stone-400">
                        {u.role || "—"}
                      </span>
                    </Td>
                    <Td>
                      {u.linkedin_url ? (
                        <a
                          href={u.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[#0a66c2] hover:underline"
                        >
                          <Linkedin size={12} /> view
                        </a>
                      ) : (
                        <span className="text-stone-500">—</span>
                      )}
                    </Td>
                    <Td>
                      <span className="font-mono">
                        {u.latest_resume_score || u.linkedin_score || "—"}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-xs text-stone-400">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-xs text-stone-400" data-testid={`last-login-${u.user_id}`}>
                        {u.last_login
                          ? new Date(u.last_login).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })
                          : <span className="text-stone-600">never</span>}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => toggleSuspend(u)}
                          data-testid={`admin-suspend-${u.user_id}`}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
                            u.suspended
                              ? "border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
                              : "border-red-600 text-red-400 hover:bg-red-600/20"
                          }`}
                        >
                          {u.suspended ? "Unsuspend" : "Suspend"}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAdmin(u)}
                          data-testid={`admin-toggle-${u.user_id}`}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
                            u.is_admin
                              ? "border-stone-600 text-stone-300 hover:bg-stone-800"
                              : "border-[#7c3aed] text-[#a78bfa] hover:bg-[#7c3aed]/20"
                          }`}
                        >
                          {u.is_admin ? "Demote" : "Make admin"}
                        </button>
                        <button
                          type="button"
                          onClick={() => resetPassword(u)}
                          disabled={busyId === u.user_id}
                          title="Reset password"
                          data-testid={`admin-reset-${u.user_id}`}
                          className="inline-flex items-center justify-center p-1.5 rounded border border-stone-700 text-stone-300 hover:bg-stone-800 disabled:opacity-50 transition-colors"
                        >
                          <KeyRound size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteUser(u)}
                          disabled={busyId === u.user_id}
                          title="Delete user"
                          data-testid={`admin-delete-${u.user_id}`}
                          className="inline-flex items-center justify-center p-1.5 rounded border border-red-700 text-red-400 hover:bg-red-600/20 disabled:opacity-50 transition-colors"
                        >
                          {busyId === u.user_id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Create User Modal ---------------- */
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "candidate", is_admin: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError("");
    setSaving(true);
    try {
      await adminCreateUser({ ...form, email: form.email.trim().toLowerCase() });
      onCreated?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <h2 className="text-lg font-medium text-stone-100">Add a user</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Full name">
            <input value={form.name} onChange={set("name")} data-testid="cu-name"
              className="admin-input" placeholder="Jane Doe" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={set("email")} data-testid="cu-email"
              className="admin-input" placeholder="jane@example.com" />
          </Field>
          <Field label="Password">
            <input type="text" value={form.password} onChange={set("password")} data-testid="cu-password"
              className="admin-input font-mono" placeholder="min 8 characters" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={set("role")} data-testid="cu-role" className="admin-input">
              <option value="candidate">Candidate</option>
              <option value="company">Company</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
            <input type="checkbox" checked={form.is_admin} onChange={set("is_admin")} data-testid="cu-admin"
              className="w-4 h-4 accent-[#7c3aed]" />
            Grant admin access
          </label>

          {error && <p className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="cu-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} Create user
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-stone-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* ---------------- Companies ---------------- */
export function AdminCompanies() {
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    adminCompanies().then(({ data }) => setRows(data));
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);

  const toggleVerify = (c) => {
    adminVerifyCompany(c.company_id, !c.verified).then(refresh);
  };
  const resetOwnerPw = async (c) => {
    if (!c.owner_user_id) { toast.error("This company has no linked owner account."); return; }
    const pw = (await confirm({ title: "Set a new password", description: `Set a new password for the owner (${c.owner_email || "—"}), min 8 chars:`, prompt: { type: "password", minLength: 8, placeholder: "New password (min 8 chars)" }, confirmText: "Save" }));
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusyId(c.company_id);
    try {
      await adminResetPassword(c.owner_user_id, pw);
      toast.success("Owner password reset.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reset password."); }
    finally { setBusyId(null); }
  };
  const deleteCompany = async (c) => {
    const delOwner = (await confirm({ title: "Please confirm", description: 
      `Delete "${c.name}"?\n\nOK = also delete the owner account (${c.owner_email || "—"}).\nCancel = keep the owner, remove only the company + its jobs.`
    , danger: true }));
    if (!(await confirm({ title: "Please confirm", description: `Confirm: permanently delete "${c.name}" and its ${c.jobs_count || 0} job(s)? This cannot be undone.`, danger: true }))) return;
    setBusyId(c.company_id);
    try {
      await adminDeleteCompany(c.company_id, delOwner);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete company."); }
    finally { setBusyId(null); }
  };

  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-companies">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Header title="Companies" subtitle="Onboard, verify, edit, or remove company workspaces." />
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="admin-add-company"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] transition-colors shrink-0"
        >
          <Building2 size={16} /> Onboard company
        </button>
      </div>

      {showCreate && (
        <CompanyModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {editing && (
        <CompanyModal
          company={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {rows.length === 0 ? (
        <Empty msg="No companies yet. Click “Onboard company” to add one." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {rows.map((c) => (
            <div key={c.company_id} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-2xl truncate">{c.name}</div>
                  <p className="text-sm text-stone-400 mt-1 truncate">{c.website || "—"}</p>
                </div>
                {c.verified ? (
                  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-emerald-400 border border-emerald-700 px-2 py-1 rounded-full shrink-0">
                    <Check size={12} /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-amber-300 border border-amber-700 px-2 py-1 rounded-full shrink-0">
                    Pending
                  </span>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-5 text-sm text-stone-300">
                <Kv k="Owner" v={c.owner_name} />
                <Kv k="Owner email" v={c.owner_email} />
                <Kv k="Jobs" v={String(c.jobs_count ?? 0)} />
                <Kv k="GSTIN" v={c.gstin} />
              </dl>
              <div className="mt-5 flex flex-wrap gap-2">
                <ActionBtn onClick={() => toggleVerify(c)} testId={`admin-verify-${c.company_id}`}
                  tone={c.verified ? "neutral" : "green"}>
                  {c.verified ? <><X size={13} /> Revoke</> : <><Check size={13} /> Verify</>}
                </ActionBtn>
                <ActionBtn onClick={() => setEditing(c)} testId={`admin-edit-co-${c.company_id}`} tone="neutral">
                  Edit
                </ActionBtn>
                <ActionBtn onClick={() => resetOwnerPw(c)} disabled={busyId === c.company_id} tone="neutral"
                  testId={`admin-co-resetpw-${c.company_id}`}>
                  <KeyRound size={13} /> Reset PW
                </ActionBtn>
                <ActionBtn onClick={() => deleteCompany(c)} disabled={busyId === c.company_id} tone="red"
                  testId={`admin-co-delete-${c.company_id}`}>
                  <Trash2 size={13} /> Delete
                </ActionBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, tone = "neutral", disabled, testId }) {
  const tones = {
    neutral: "border-stone-600 text-stone-300 hover:bg-stone-800",
    green: "border-emerald-600 text-emerald-400 hover:bg-emerald-600/20",
    red: "border-red-700 text-red-400 hover:bg-red-600/20",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-full border transition-colors disabled:opacity-50 ${tones[tone]}`}>
      {children}
    </button>
  );
}

function CompanyModal({ company, onClose, onSaved }) {
  const isEdit = !!company;
  const [form, setForm] = useState({
    name: company?.name || "",
    website: company?.website || "",
    domain: company?.domain || "",
    about: company?.about || "",
    gstin: company?.gstin || "",
    verified: company?.verified ?? true,
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Company name is required."); return; }
    if (!isEdit) {
      if (!form.owner_name.trim() || !form.owner_email.trim()) { setError("Owner name and email are required."); return; }
      if (form.owner_password.length < 8) { setError("Owner password must be at least 8 characters."); return; }
    }
    setError(""); setSaving(true);
    try {
      if (isEdit) {
        await adminUpdateCompany(company.company_id, {
          name: form.name, website: form.website, domain: form.domain,
          about: form.about, gstin: form.gstin, verified: form.verified,
        });
      } else {
        await adminCreateCompany({ ...form, owner_email: form.owner_email.trim().toLowerCase() });
      }
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save company.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 sticky top-0 bg-[#1c1917]">
          <h2 className="text-lg font-medium text-stone-100">{isEdit ? "Edit company" : "Onboard a company"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Company name"><input value={form.name} onChange={set("name")} data-testid="cc-name" className="admin-input" placeholder="Acme Inc." /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Website"><input value={form.website} onChange={set("website")} className="admin-input" placeholder="https://acme.com" /></Field>
            <Field label="Email domain"><input value={form.domain} onChange={set("domain")} className="admin-input" placeholder="acme.com" /></Field>
          </div>
          <Field label="GSTIN"><input value={form.gstin} onChange={set("gstin")} className="admin-input" placeholder="optional" /></Field>
          <Field label="About"><textarea value={form.about} onChange={set("about")} rows={2} className="admin-input" placeholder="optional" /></Field>

          {!isEdit && (
            <div className="pt-2 border-t border-stone-800 space-y-4">
              <p className="text-xs font-mono uppercase tracking-widest text-stone-500">Owner account</p>
              <Field label="Owner name"><input value={form.owner_name} onChange={set("owner_name")} data-testid="cc-owner-name" className="admin-input" placeholder="Jane Doe" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner email"><input type="email" value={form.owner_email} onChange={set("owner_email")} data-testid="cc-owner-email" className="admin-input" placeholder="jane@acme.com" /></Field>
                <Field label="Password"><input type="text" value={form.owner_password} onChange={set("owner_password")} data-testid="cc-owner-pw" className="admin-input font-mono" placeholder="min 8 chars" /></Field>
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
            <input type="checkbox" checked={form.verified} onChange={set("verified")} className="w-4 h-4 accent-emerald-500" />
            Mark as verified
          </label>

          {error && <p className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">Cancel</button>
            <button type="submit" disabled={saving} data-testid="cc-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEdit ? "Save changes" : "Create company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Agencies ---------------- */
export function AdminAgencies() {
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    adminAgencies().then(({ data }) => setRows(data));
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);

  const resetOwnerPw = async (a) => {
    if (!a.owner_user_id) { toast.error("This agency has no linked owner account."); return; }
    const pw = (await confirm({ title: "Set a new password", description: `Set a new password for the owner (${a.owner_email || "—"}), min 8 chars:`, prompt: { type: "password", minLength: 8, placeholder: "New password (min 8 chars)" }, confirmText: "Save" }));
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusyId(a.agency_id);
    try {
      await adminResetPassword(a.owner_user_id, pw);
      toast.success("Owner password reset.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reset password."); }
    finally { setBusyId(null); }
  };
  const deleteAgency = async (a) => {
    const delOwner = (await confirm({ title: "Please confirm", description: 
      `Delete "${a.name}"?\n\nOK = also delete the owner account (${a.owner_email || "—"}).\nCancel = keep the owner, remove only the agency + its clients/submissions.`
    , danger: true }));
    if (!(await confirm({ title: "Please confirm", description: `Confirm: permanently delete "${a.name}" and its ${a.clients_count || 0} client(s) and ${a.submissions_count || 0} submission(s)? This cannot be undone.`, danger: true }))) return;
    setBusyId(a.agency_id);
    try {
      await adminDeleteAgency(a.agency_id, delOwner);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete agency."); }
    finally { setBusyId(null); }
  };

  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-agencies">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Header title="Agencies" subtitle="Onboard recruiting agencies, set their plan/credits, edit, or remove." />
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="admin-add-agency"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] transition-colors shrink-0"
        >
          <Briefcase size={16} /> Onboard agency
        </button>
      </div>

      {showCreate && (
        <AgencyModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {editing && (
        <AgencyModal
          agency={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {rows.length === 0 ? (
        <Empty msg="No agencies yet. Click “Onboard agency” to add one." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {rows.map((a) => (
            <div key={a.agency_id} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-2xl truncate">{a.name}</div>
                  <p className="text-sm text-stone-400 mt-1 truncate">{a.website || "—"}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-amber-300 border border-amber-700 px-2 py-1 rounded-full shrink-0">
                  {a.plan || "free"}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-5 text-sm text-stone-300">
                <Kv k="Owner" v={a.owner_name} />
                <Kv k="Owner email" v={a.owner_email} />
                <Kv k="Clients" v={String(a.clients_count ?? 0)} />
                <Kv k="Submissions" v={String(a.submissions_count ?? 0)} />
                <Kv k="Credits" v={String(a.credits ?? 0)} />
              </dl>
              <div className="mt-5 flex flex-wrap gap-2">
                <ActionBtn onClick={() => setEditing(a)} testId={`admin-edit-ag-${a.agency_id}`} tone="neutral">
                  Edit
                </ActionBtn>
                <ActionBtn onClick={() => resetOwnerPw(a)} disabled={busyId === a.agency_id} tone="neutral"
                  testId={`admin-ag-resetpw-${a.agency_id}`}>
                  <KeyRound size={13} /> Reset PW
                </ActionBtn>
                <ActionBtn onClick={() => deleteAgency(a)} disabled={busyId === a.agency_id} tone="red"
                  testId={`admin-ag-delete-${a.agency_id}`}>
                  <Trash2 size={13} /> Delete
                </ActionBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgencyModal({ agency, onClose, onSaved }) {
  const isEdit = !!agency;
  const [form, setForm] = useState({
    name: agency?.name || "",
    website: agency?.website || "",
    about: agency?.about || "",
    plan: agency?.plan || "free",
    credits: agency?.credits ?? 5,
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Agency name is required."); return; }
    if (!isEdit) {
      if (!form.owner_name.trim() || !form.owner_email.trim()) { setError("Owner name and email are required."); return; }
      if (form.owner_password.length < 8) { setError("Owner password must be at least 8 characters."); return; }
    }
    setError(""); setSaving(true);
    try {
      if (isEdit) {
        await adminUpdateAgency(agency.agency_id, {
          name: form.name, website: form.website, about: form.about,
          plan: form.plan, credits: Number(form.credits) || 0,
        });
      } else {
        await adminCreateAgency({
          ...form,
          credits: Number(form.credits) || 0,
          owner_email: form.owner_email.trim().toLowerCase(),
        });
      }
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save agency.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 sticky top-0 bg-[#1c1917]">
          <h2 className="text-lg font-medium text-stone-100">{isEdit ? "Edit agency" : "Onboard an agency"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Agency name"><input value={form.name} onChange={set("name")} data-testid="ca-name" className="admin-input" placeholder="Apex Talent Partners" /></Field>
          <Field label="Website"><input value={form.website} onChange={set("website")} className="admin-input" placeholder="https://apextalent.com" /></Field>
          <Field label="About"><textarea value={form.about} onChange={set("about")} rows={2} className="admin-input" placeholder="optional" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plan">
              <select value={form.plan} onChange={set("plan")} data-testid="ca-plan" className="admin-input">
                <option value="free">Free</option>
                <option value="growth">Growth</option>
                <option value="scale">Scale</option>
              </select>
            </Field>
            <Field label="Credits"><input type="number" min="0" value={form.credits} onChange={set("credits")} data-testid="ca-credits" className="admin-input font-mono" /></Field>
          </div>

          {!isEdit && (
            <div className="pt-2 border-t border-stone-800 space-y-4">
              <p className="text-xs font-mono uppercase tracking-widest text-stone-500">Owner account</p>
              <Field label="Owner name"><input value={form.owner_name} onChange={set("owner_name")} data-testid="ca-owner-name" className="admin-input" placeholder="Jane Doe" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner email"><input type="email" value={form.owner_email} onChange={set("owner_email")} data-testid="ca-owner-email" className="admin-input" placeholder="jane@apextalent.com" /></Field>
                <Field label="Password"><input type="text" value={form.owner_password} onChange={set("owner_password")} data-testid="ca-owner-pw" className="admin-input font-mono" placeholder="min 8 chars" /></Field>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">Cancel</button>
            <button type="submit" disabled={saving} data-testid="ca-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEdit ? "Save changes" : "Create agency"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Colleges ---------------- */
export function AdminColleges() {
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [teamFor, setTeamFor] = useState(null);
  const [modulesFor, setModulesFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { adminColleges().then(({ data }) => setRows(data)); }, [tick]);
  const refresh = () => setTick((t) => t + 1);

  const resetOwnerPw = async (c) => {
    if (!c.owner_user_id) { toast.error("This college has no linked owner account."); return; }
    const pw = (await confirm({ title: "Set a new password", description: `Set a new password for the placement officer (${c.owner_email || "—"}), min 8 chars:`, prompt: { type: "password", minLength: 8, placeholder: "New password (min 8 chars)" }, confirmText: "Save" }));
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusyId(c.college_id);
    try { await adminResetPassword(c.owner_user_id, pw); toast.success("Owner password reset."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to reset password."); }
    finally { setBusyId(null); }
  };
  const deleteCollege = async (c) => {
    const delOwner = (await confirm({ title: "Please confirm", description: 
      `Delete "${c.name}"?\n\nOK = also delete the owner account (${c.owner_email || "—"}).\nCancel = keep the owner, remove only the college + its drives. Students are detached, not deleted.`
    , danger: true }));
    if (!(await confirm({ title: "Please confirm", description: `Confirm: permanently delete "${c.name}" and its ${c.drives_count || 0} drive(s)? This cannot be undone.`, danger: true }))) return;
    setBusyId(c.college_id);
    try { await adminDeleteCollege(c.college_id, delOwner); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete college."); }
    finally { setBusyId(null); }
  };

  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-colleges">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Header title="Colleges" subtitle="Onboard placement cells, set their plan, edit, or remove." />
        <button type="button" onClick={() => setShowCreate(true)} data-testid="admin-add-college"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] transition-colors shrink-0">
          <GraduationCap size={16} /> Onboard college
        </button>
      </div>

      {showCreate && <CollegeModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refresh(); }} />}
      {editing && <CollegeModal college={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {teamFor && <CollegeTeamModal college={teamFor} onClose={() => setTeamFor(null)} />}
      {modulesFor && <CollegeModulesModal college={modulesFor} onClose={() => setModulesFor(null)} onSaved={() => { setModulesFor(null); refresh(); }} />}

      {rows.length === 0 ? (
        <Empty msg="No colleges yet. Click “Onboard college” to add one." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {rows.map((c) => (
            <div key={c.college_id} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-2xl truncate">{c.name}</div>
                  <p className="text-sm text-stone-400 mt-1 truncate">{c.website || "—"}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-amber-300 border border-amber-700 px-2 py-1 rounded-full shrink-0">{c.plan || "free"}</span>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-5 text-sm text-stone-300">
                <Kv k="Owner" v={c.owner_name} />
                <Kv k="Owner email" v={c.owner_email} />
                <Kv k="Students" v={String(c.students_count ?? 0)} />
                <Kv k="Drives" v={String(c.drives_count ?? 0)} />
                <Kv k="Invite code" v={c.invite_code} />
                <Kv k="Modules" v={c.modules ? `${Object.values(c.modules).filter(Boolean).length}/${Object.keys(c.modules).length} on` : "—"} />
                <Kv k="₹ / student / yr" v={c.price_per_student_inr ? `₹${Number(c.price_per_student_inr).toLocaleString("en-IN")}` : "not set"} />
                <Kv k="Student prices" v={c.student_price_overrides && Object.keys(c.student_price_overrides).length ? "custom" : "platform default"} />
                <Kv k="Recruiter credits" v={Number(c.recruiter_credits || 0).toLocaleString("en-IN")} />
                <Kv k="Seats" v={c.seats ? `${c.seats.active}/${c.seats.licensed}${c.seats.locked ? ` · ${c.seats.locked} locked` : ""}` : "—"} />
                <Kv k="Course fee %" v={c.course_margin_pct != null ? `${c.course_margin_pct}%` : "—"} />
                <Kv k="Course revenue" v={c.course_sales ? `${c.course_sales.count} sold · ₹${Number(c.course_sales.wishire_cut_inr||0).toLocaleString("en-IN")} to us` : "—"} />
                {c.pending_license_request && (
                  <Kv k="Seat request" v={`+${c.pending_license_request.seats} pending`} />
                )}
              </dl>
              <div className="mt-5 flex flex-wrap gap-2">
                <ActionBtn onClick={() => setEditing(c)} testId={`admin-edit-col-${c.college_id}`} tone="neutral">Edit</ActionBtn>
                <ActionBtn onClick={() => setTeamFor(c)} testId={`admin-col-team-${c.college_id}`} tone="neutral"><UsersIcon size={13} /> Team</ActionBtn>
                <ActionBtn onClick={() => setModulesFor(c)} testId={`admin-col-modules-${c.college_id}`} tone="neutral"><SlidersHorizontal size={13} /> Modules</ActionBtn>
                <ActionBtn onClick={() => resetOwnerPw(c)} disabled={busyId === c.college_id} tone="neutral" testId={`admin-col-resetpw-${c.college_id}`}><KeyRound size={13} /> Reset PW</ActionBtn>
                <ActionBtn onClick={() => deleteCollege(c)} disabled={busyId === c.college_id} tone="red" testId={`admin-col-delete-${c.college_id}`}><Trash2 size={13} /> Delete</ActionBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CollegeModal({ college, onClose, onSaved }) {
  const isEdit = !!college;
  const [form, setForm] = useState({
    name: college?.name || "",
    website: college?.website || "",
    about: college?.about || "",
    plan: college?.plan || "free",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    price_per_student_inr: college?.price_per_student_inr ?? "",
    licensed_seats: college?.licensed_seats ?? "",
    course_margin_pct: college?.course_margin_pct ?? "",
    ov_pack: college?.student_price_overrides?.practice_pack_inr ?? "",
    ov_agent: college?.student_price_overrides?.wishagent_monthly_inr ?? "",
    ov_prep: college?.student_price_overrides?.drive_prep_inr ?? "",
    ov_resume: college?.student_price_overrides?.resume_build_inr ?? "",
    add_credits: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("College name is required."); return; }
    if (!isEdit) {
      if (!form.owner_name.trim() || !form.owner_email.trim()) { setError("Owner name and email are required."); return; }
      if (form.owner_password.length < 8) { setError("Owner password must be at least 8 characters."); return; }
    }
    setError(""); setSaving(true);
    try {
      if (isEdit) {
        const overrides = {};
        if (form.ov_pack !== "") overrides.practice_pack_inr = parseInt(form.ov_pack, 10);
        if (form.ov_agent !== "") overrides.wishagent_monthly_inr = parseInt(form.ov_agent, 10);
        if (form.ov_prep !== "") overrides.drive_prep_inr = parseInt(form.ov_prep, 10);
        if (form.ov_resume !== "") overrides.resume_build_inr = parseInt(form.ov_resume, 10);
        const payload = {
          name: form.name, website: form.website, about: form.about, plan: form.plan,
          price_per_student_inr: form.price_per_student_inr === "" ? 0 : parseInt(form.price_per_student_inr, 10),
          ...(form.licensed_seats !== "" && Number.isInteger(parseInt(form.licensed_seats, 10)) ? { licensed_seats: parseInt(form.licensed_seats, 10) } : {}),
          ...(form.course_margin_pct !== "" && !Number.isNaN(parseFloat(form.course_margin_pct)) ? { course_margin_pct: parseFloat(form.course_margin_pct) } : {}),
          student_price_overrides: overrides, // {} reverts to platform defaults
        };
        if (form.add_credits !== "" && parseInt(form.add_credits, 10)) payload.add_credits = parseInt(form.add_credits, 10);
        await adminUpdateCollege(college.college_id, payload);
      } else {
        await adminCreateCollege({ ...form, owner_email: form.owner_email.trim().toLowerCase() });
      }
      onSaved?.();
    } catch (err) { setError(err?.response?.data?.detail || "Failed to save college."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 sticky top-0 bg-[#1c1917]">
          <h2 className="text-lg font-medium text-stone-100">{isEdit ? "Edit college" : "Onboard a college"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="College / institution name"><input value={form.name} onChange={set("name")} data-testid="cl-name" className="admin-input" placeholder="Demo Institute of Technology" /></Field>
          <Field label="Website"><input value={form.website} onChange={set("website")} className="admin-input" placeholder="https://college.edu" /></Field>
          <Field label="Plan">
            <select value={form.plan} onChange={set("plan")} data-testid="cl-plan" className="admin-input">
              <option value="free">Starter (free)</option>
              <option value="growth">Department</option>
              <option value="scale">Institution</option>
            </select>
          </Field>
          {isEdit && (
            <div className="pt-2 border-t border-stone-800 space-y-4">
              <p className="text-xs font-mono uppercase tracking-widest text-stone-500">Pricing — this college only (never shown to the college)</p>
              <Field label={`Licensed seats (currently ${college?.seats ? `${college.seats.active}/${college.seats.licensed} used` : "plan default"})`}>
                <input type="number" min="0" value={form.licensed_seats} onChange={set("licensed_seats")}
                  data-testid="cl-seats" className="admin-input" placeholder="e.g. 100 — students beyond this stay locked" />
              </Field>
              <Field label="Course sale fee % (Wishire's cut on each paid course; default 20%)">
                <input type="number" min="0" max="100" value={form.course_margin_pct} onChange={set("course_margin_pct")}
                  data-testid="cl-course-margin" className="admin-input" placeholder="e.g. 20" />
              </Field>
              <Field label="Contract price (₹ / student / year)">
                <input type="number" min="0" value={form.price_per_student_inr} onChange={set("price_per_student_inr")}
                  data-testid="cl-price-student" className="admin-input" placeholder="e.g. 1500" />
              </Field>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Practice Pack ₹">
                  <input type="number" min="0" value={form.ov_pack} onChange={set("ov_pack")} className="admin-input" placeholder="default" />
                </Field>
                <Field label="WishAgent ₹/mo">
                  <input type="number" min="0" value={form.ov_agent} onChange={set("ov_agent")} className="admin-input" placeholder="default" />
                </Field>
                <Field label="Drive Prep ₹">
                  <input type="number" min="0" value={form.ov_prep} onChange={set("ov_prep")} className="admin-input" placeholder="default" />
                </Field>
                <Field label="Resume build ₹">
                  <input type="number" min="0" value={form.ov_resume} onChange={set("ov_resume")} data-testid="cl-ov-resume" className="admin-input" placeholder="default" />
                </Field>
              </div>
              <p className="text-xs text-stone-500">Student-side prices for this college's students. Leave blank to charge the platform defaults (Admin → Pricing). Resume build: 0 = free for this college.</p>
              <Field label={`Add recruiter credits (balance: ${Number(college?.recruiter_credits || 0).toLocaleString("en-IN")})`}>
                <input type="number" value={form.add_credits} onChange={set("add_credits")}
                  data-testid="cl-add-credits" className="admin-input" placeholder="e.g. 1000 (₹3,500 pack) — negative to claw back" />
              </Field>
              <p className="text-xs text-stone-500">Credits meter Apify/AI usage in Recruiters: company lookup 1 · Find HR 2 · one contact's email 3 · verified emails 5 · AI draft 1. Suggested pack ₹3,500 → 1,000 credits.</p>
            </div>
          )}
          {!isEdit && (
            <div className="pt-2 border-t border-stone-800 space-y-4">
              <p className="text-xs font-mono uppercase tracking-widest text-stone-500">Placement officer account</p>
              <Field label="Owner name"><input value={form.owner_name} onChange={set("owner_name")} data-testid="cl-owner-name" className="admin-input" placeholder="Jane Doe" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner email"><input type="email" value={form.owner_email} onChange={set("owner_email")} data-testid="cl-owner-email" className="admin-input" placeholder="tnp@college.edu" /></Field>
                <Field label="Password"><input type="text" value={form.owner_password} onChange={set("owner_password")} data-testid="cl-owner-pw" className="admin-input font-mono" placeholder="min 8 chars" /></Field>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">Cancel</button>
            <button type="submit" disabled={saving} data-testid="cl-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEdit ? "Save changes" : "Create college"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CollegeModulesModal({ college, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);         // {modules: [...], plan_defaults: {plan: [keys]}}
  const [enabled, setEnabled] = useState(college.modules || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const plan = college.plan || "free";

  useEffect(() => {
    adminModuleCatalog().then(({ data }) => setCatalog(data)).catch(() => setError("Failed to load module catalog."));
  }, []);

  const planDefaults = new Set(catalog?.plan_defaults?.[plan] || []);
  const toggle = (key) => setEnabled((m) => ({ ...m, [key]: !m[key] }));
  const resetToPlan = () => {
    const next = {};
    (catalog?.modules || []).forEach((m) => { next[m.key] = planDefaults.has(m.key); });
    setEnabled(next);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      // Store only the deltas vs the plan defaults — a plan change later
      // won't be fought by stale overrides that merely restated the default.
      const overrides = {};
      (catalog?.modules || []).forEach((m) => {
        const eff = !!enabled[m.key];
        if (eff !== planDefaults.has(m.key)) overrides[m.key] = eff;
      });
      await adminUpdateCollege(college.college_id, { module_overrides: overrides });
      onSaved?.();
    } catch (e) { setError(e?.response?.data?.detail || "Failed to save modules."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="admin-modules-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 sticky top-0 bg-[#1c1917]">
          <div>
            <h2 className="text-lg font-medium text-stone-100">Dashboard modules</h2>
            <p className="text-xs text-stone-500 mt-0.5">{college.name} · <span className="uppercase font-mono text-amber-300">{plan}</span> plan</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-2.5">
          {!catalog && !error && <p className="text-sm text-stone-500">Loading modules…</p>}
          {(catalog?.modules || []).map((m) => {
            const on = !!enabled[m.key];
            const isDefault = on === planDefaults.has(m.key);
            return (
              <button key={m.key} type="button" onClick={() => toggle(m.key)} data-testid={`module-toggle-${m.key}`}
                className={`w-full flex items-center gap-4 rounded-xl border px-4 py-3 text-left transition-colors ${on ? "border-emerald-800 bg-emerald-900/20" : "border-stone-800 bg-stone-900/40 opacity-70"}`}>
                <span className={`shrink-0 w-9 h-5 rounded-full relative transition-colors ${on ? "bg-emerald-500" : "bg-stone-700"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-stone-100">{m.label}</span>
                    {!isDefault && <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-800">override</span>}
                  </span>
                  <span className="block text-xs text-stone-500 mt-0.5">{m.desc}</span>
                </span>
              </button>
            );
          })}
          {error && <p className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex items-center justify-between gap-3 pt-3">
            <button type="button" onClick={resetToPlan} disabled={!catalog} className="text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2">Reset to plan defaults</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">Cancel</button>
              <button type="button" onClick={save} disabled={saving || !catalog} data-testid="modules-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin" />} Save modules
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CollegeTeamModal({ college, onClose }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", member_role: "coordinator", branch: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => adminCollegeTeam(college.college_id).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const invite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setMsg({ ok: false, text: "Name and email are required." }); return; }
    if (form.member_role === "hod" && !form.branch.trim()) { setMsg({ ok: false, text: "An HOD needs a department (branch)." }); return; }
    setBusy(true); setMsg(null);
    try {
      await adminCollegeTeamInvite(college.college_id, {
        name: form.name.trim(), email: form.email.trim().toLowerCase(),
        member_role: form.member_role, branch: form.branch.trim() || undefined,
      });
      setMsg({ ok: true, text: `Invited ${form.email.trim()} as ${form.member_role}. They set a password via "Forgot password".` });
      setForm({ name: "", email: "", member_role: "coordinator", branch: "" });
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Could not invite." }); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!(await confirm({ title: "Please confirm", description: `Remove ${m.name || m.email} from ${college.name}'s team?`, danger: true }))) return;
    try { await adminCollegeTeamRemove(college.college_id, m.user_id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-2xl rounded-2xl bg-[#1c1917] border border-stone-800 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <div>
            <h2 className="text-lg font-medium text-stone-100">Placement team</h2>
            <p className="text-xs text-stone-400">{college.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">
          <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="admin-team-name" className="admin-input" placeholder="Name" />
            <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-team-email" className="admin-input" placeholder="email@college.edu" />
            <select value={form.member_role} onChange={(e) => setForm((f) => ({ ...f, member_role: e.target.value }))} data-testid="admin-team-role" className="admin-input">
              <option value="coordinator">Coordinator</option>
              <option value="hod">HOD</option>
            </select>
            <input value={form.branch} onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))} data-testid="admin-team-branch" className="admin-input" placeholder={form.member_role === "hod" ? "Department *" : "Branch (opt)"} />
            <button type="submit" disabled={busy} data-testid="admin-team-invite"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Invite
            </button>
          </form>
          {msg && <p className={`mt-3 text-sm rounded-lg px-3 py-2 border ${msg.ok ? "bg-emerald-900/30 border-emerald-800 text-emerald-300" : "bg-red-900/30 border-red-800 text-red-400"}`}>{msg.text}</p>}
          <p className="mt-2 text-xs text-stone-500">HODs see only their department; coordinators and the owner see the whole college.</p>

          <div className="mt-5 space-y-2">
            {rows === null ? <p className="text-stone-500 text-sm">Loading…</p> : rows.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-3 rounded-xl border border-stone-800 bg-stone-900/60 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-100 truncate">{m.name} {m.branch && <span className="text-stone-500">· {m.branch}</span>}</p>
                  <p className="text-xs text-stone-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${m.member_role === "owner" ? "bg-[#C2410C] text-white" : m.member_role === "hod" ? "bg-amber-400/15 text-amber-300 border border-amber-700" : "bg-stone-800 text-stone-300"}`}>{m.member_role}</span>
                  {m.member_role !== "owner" && (
                    <button onClick={() => remove(m)} data-testid={`admin-team-remove-${m.user_id}`} className="p-1.5 rounded-lg text-stone-500 hover:text-red-400 hover:bg-red-900/30"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Pricing ---------------- */
const PLAN_NUM_FIELDS = {
  company: [
    { key: "price_inr", label: "Price ₹/mo" },
    { key: "job_slots", label: "Job slots" },
    { key: "monthly_credits", label: "Monthly credits" },
  ],
  agency: [
    { key: "price_inr", label: "Price ₹/mo" },
    { key: "client_slots", label: "Client slots" },
    { key: "monthly_credits", label: "Monthly credits" },
  ],
  college: [
    { key: "price_inr", label: "Price ₹/yr" },
    { key: "student_cap", label: "Student cap" },
  ],
};
const PLAN_BOOL_FIELDS = {
  company: [{ key: "talent_pool", label: "Talent pool" }, { key: "screening", label: "Screening" }],
  agency: [{ key: "talent_pool", label: "Talent pool" }, { key: "public_dossier", label: "Public dossier" }],
  college: [{ key: "drives", label: "Drives" }, { key: "reports", label: "Reports" }],
};

/* Student-side amounts (practice pack, WishAgent, drive prep) — one place,
   applied instantly across the platform via pricing_config. */
function FeatureFlagsCard() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminFeatureFlags().then(({ data }) => setFlags(data.flags)).catch(() => {}); }, []); // eslint-disable-line
  const toggle = async (key) => {
    setBusy(true);
    try { const { data } = await adminSaveFeatureFlags({ [key]: !flags[key] }); setFlags(data.flags); }
    finally { setBusy(false); }
  };
  if (!flags) return null;
  return (
    <div className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6 mb-8" data-testid="feature-flags-card">
      <h2 className="font-display text-xl text-stone-100">Feature switches</h2>
      <p className="text-xs text-stone-500 mt-1">Platform-wide. Off = the feature is hidden from students and its APIs are blocked.</p>
      <div className="mt-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-stone-200">WishAgent (auto-apply)</p>
          <p className="text-xs text-stone-500">The agent that applies to external jobs for candidates (₹699/mo).</p>
        </div>
        <button onClick={() => toggle("wishagent_enabled")} disabled={busy} data-testid="flag-wishagent"
          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${flags.wishagent_enabled ? "bg-emerald-600" : "bg-stone-700"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${flags.wishagent_enabled ? "left-[26px]" : "left-0.5"}`} />
        </button>
      </div>
      <div className="mt-4 pt-4 border-t border-stone-800 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-stone-200">Paid resume builds</p>
          <p className="text-xs text-stone-500">On = each student gets 1 free AI resume, then pays the "AI Resume build" price below (per-college override in Colleges → Edit). Off = unlimited free builds.</p>
        </div>
        <button onClick={() => toggle("resume_paid_enabled")} disabled={busy} data-testid="flag-resume-paid"
          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${flags.resume_paid_enabled ? "bg-emerald-600" : "bg-stone-700"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${flags.resume_paid_enabled ? "left-[26px]" : "left-0.5"}`} />
        </button>
      </div>
      <div className="mt-4 pt-4 border-t border-stone-800 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-stone-200">Public self-signup</p>
          <p className="text-xs text-stone-500">Off = closed platform: accounts come only from college imports, team invites, or this panel. Google sign-in works for existing accounts but creates none.</p>
        </div>
        <button onClick={() => toggle("self_signup_enabled")} disabled={busy} data-testid="flag-self-signup"
          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${flags.self_signup_enabled ? "bg-emerald-600" : "bg-stone-700"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${flags.self_signup_enabled ? "left-[26px]" : "left-0.5"}`} />
        </button>
      </div>
    </div>
  );
}

function StudentPricesCard() {
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const LABELS = {
    practice_pack_inr: "Practice Pack (5 interviews + 5 tests)",
    wishagent_monthly_inr: "WishAgent auto-apply (per month)",
    drive_prep_inr: "Drive Prep (3 past-pattern papers per drive)",
    resume_build_inr: "AI Resume build (first free, then per build)",
  };
  useEffect(() => {
    adminStudentPrices().then(({ data }) => setPrices(data.prices)).catch(() => setMsg("Failed to load student prices."));
  }, []);
  const save = async () => {
    setSaving(true); setMsg("");
    try { const { data } = await adminSaveStudentPrices(prices); setPrices(data.prices); setMsg("Saved — applied platform-wide immediately."); }
    catch (e) { setMsg(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-8 rounded-2xl border border-stone-800 bg-[#1c1917] p-6" data-testid="student-prices">
      <h2 className="text-lg font-medium text-stone-100">Student-side prices</h2>
      <p className="text-xs text-stone-500 mt-1">Platform defaults — every paywall and purchase uses these unless a college has its own prices set (Colleges → Edit → Pricing).</p>
      {!prices ? <p className="mt-4 text-stone-500 text-sm">{msg || "Loading…"}</p> : (
        <>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {Object.keys(LABELS).map((k) => (
              <div key={k}>
                <label className="text-xs text-stone-400">{LABELS[k]}</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-stone-500">₹</span>
                  <input type="number" min="0" value={prices[k] ?? ""} data-testid={`price-${k}`}
                    onChange={(e) => setPrices((p) => ({ ...p, [k]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-700 text-sm text-stone-100 font-mono" />
                </div>
              </div>
            ))}
          </div>
          {msg && <p className="mt-3 text-sm text-amber-300">{msg}</p>}
          <button onClick={save} disabled={saving} data-testid="student-prices-save"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />} Save student prices
          </button>
        </>
      )}
    </div>
  );
}

export function AdminPricing() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("company");
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try { const { data } = await adminPricing(); setData(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  if (!data) return <SkeletonGrid />;

  const block = data[kind] || { plans: [], credit_packs: [] };

  const savePlan = async (planId, patch) => {
    setMsg(null);
    try {
      await adminUpdatePlan(kind, planId, patch);
      setMsg(`Saved ${kind} · ${planId}.`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not save plan."); }
  };
  const savePacks = async (packs) => {
    setMsg(null);
    try {
      await adminUpdateCreditPacks(kind, packs);
      setMsg(`Saved ${kind} credit packs.`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not save packs."); }
  };

  return (
    <div data-testid="admin-pricing">
      <Header title="Pricing" subtitle="Edit subscription plans and credit packs. Changes apply immediately and persist." />

      <FeatureFlagsCard />
      <StudentPricesCard />

      <div className="mt-6 flex items-center gap-2">
        {["company", "agency", "college"].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            data-testid={`pricing-kind-${k}`}
            className={`text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${
              kind === k ? "bg-[#C2410C] border-[#C2410C] text-white" : "border-stone-700 text-stone-300 hover:bg-stone-800/60"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {msg && <p className="mt-5 text-sm rounded-lg bg-emerald-900/30 border border-emerald-800 text-emerald-300 px-4 py-2.5">{msg}</p>}

      <div className="grid md:grid-cols-3 gap-5 mt-6">
        {block.plans.map((p) => (
          <PlanEditor key={p.id} kind={kind} plan={p} onSave={savePlan} />
        ))}
      </div>

      {kind !== "college" && <CreditPacksEditor key={kind} packs={block.credit_packs} onSave={savePacks} />}
    </div>
  );
}

function PlanEditor({ kind, plan, onSave }) {
  const [form, setForm] = useState({ ...plan });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    const patch = { name: form.name, tagline: form.tagline };
    PLAN_NUM_FIELDS[kind].forEach((f) => { patch[f.key] = Number(form[f.key]) || 0; });
    PLAN_BOOL_FIELDS[kind].forEach((f) => { patch[f.key] = !!form[f.key]; });
    await onSave(plan.id, patch);
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6">
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{plan.id}</p>
      <input
        value={form.name || ""} onChange={(e) => set("name", e.target.value)}
        data-testid={`plan-name-${plan.id}`} className="admin-input mt-1 font-display text-lg"
      />
      <div className="grid grid-cols-3 gap-2 mt-4">
        {PLAN_NUM_FIELDS[kind].map((f) => (
          <div key={f.key}>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-stone-500 mb-1">{f.label}</label>
            <input
              type="number" min="0" value={form[f.key] ?? 0}
              onChange={(e) => set(f.key, e.target.value)}
              data-testid={`plan-${plan.id}-${f.key}`} className="admin-input font-mono"
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-4">
        {PLAN_BOOL_FIELDS[kind].map((f) => (
          <label key={f.key} className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
            <input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)}
              data-testid={`plan-${plan.id}-${f.key}`} className="w-4 h-4 accent-[#C2410C]" />
            {f.label}
          </label>
        ))}
      </div>
      <input
        value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
        placeholder="Tagline" className="admin-input mt-4 text-sm"
      />
      <button
        type="button" onClick={submit} disabled={saving}
        data-testid={`plan-save-${plan.id}`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save plan
      </button>
    </div>
  );
}

function CreditPacksEditor({ packs, onSave }) {
  const [rows, setRows] = useState(packs.map((p) => ({ ...p })));
  const [saving, setSaving] = useState(false);
  const setRow = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const addRow = () => setRows((r) => [...r, { credits: 0, price_inr: 0 }]);
  const removeRow = (i) => setRows((r) => r.filter((_, j) => j !== i));

  const submit = async () => {
    setSaving(true);
    const clean = rows.map((r) => ({ credits: Number(r.credits) || 0, price_inr: Number(r.price_inr) || 0 })).filter((r) => r.credits > 0);
    await onSave(clean);
    setSaving(false);
  };

  return (
    <div className="mt-8 rounded-2xl border border-stone-800 bg-[#1c1917] p-6">
      <h3 className="font-display text-xl mb-4">Credit packs</h3>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-stone-500 mb-1">Credits</label>
              <input type="number" min="1" value={r.credits} onChange={(e) => setRow(i, "credits", e.target.value)}
                data-testid={`pack-credits-${i}`} className="admin-input font-mono w-28" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-stone-500 mb-1">Price ₹</label>
              <input type="number" min="0" value={r.price_inr} onChange={(e) => setRow(i, "price_inr", e.target.value)}
                data-testid={`pack-price-${i}`} className="admin-input font-mono w-32" />
            </div>
            <button type="button" onClick={() => removeRow(i)} className="mt-5 p-2 rounded-lg border border-red-800 text-red-400 hover:bg-red-900/30">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-5">
        <button type="button" onClick={addRow} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">+ Add pack</button>
        <button type="button" onClick={submit} disabled={saving} data-testid="packs-save"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save packs
        </button>
      </div>
    </div>
  );
}

/* ---------------- Waitlist ---------------- */
export function AdminWaitlist() {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminWaitlist().then(({ data }) => setRows(data)); }, []);
  if (rows === null) return <SkeletonGrid />;

  const exportCsv = () => {
    const lines = ["email,role,source,name,created_at"];
    rows.forEach((r) =>
      lines.push([r.email, r.role, r.source || "", r.name || "", r.created_at || ""].map(csv).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `wishire-waitlist-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-waitlist">
      <Header
        title="Waitlist"
        subtitle={`${rows.length} signups`}
        right={
          <button
            type="button"
            onClick={exportCsv}
            data-testid="admin-waitlist-export"
            className="text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full bg-[#C2410C] hover:bg-[#9a3308] transition-colors"
          >
            Export CSV
          </button>
        }
      />
      <Table
        headers={["Email", "Role", "Name", "Source", "Joined"]}
        rows={rows.map((r) => [
          r.email,
          <span key="role" className="text-xs font-mono uppercase">{r.role}</span>,
          r.name || "—",
          r.source || "—",
          r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
        ])}
      />
    </div>
  );
}

/* ---------------- Jobs ---------------- */
export function AdminJobs() {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminJobs().then(({ data }) => setRows(data)); }, []);
  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-jobs">
      <Header title="All jobs" subtitle={`${rows.length} jobs platform-wide.`} />
      <Table
        headers={["Title", "Company", "Status", "Posted"]}
        rows={rows.map((j) => [
          j.title,
          j.company_name || j.company_id,
          <span key="status" className={`text-xs font-mono uppercase tracking-widest ${j.status === "open" ? "text-emerald-400" : "text-stone-400"}`}>
            {j.status}
          </span>,
          j.created_at ? new Date(j.created_at).toLocaleDateString() : "—",
        ])}
      />
    </div>
  );
}

/* ---------------- Leads ---------------- */
export function AdminLeads() {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminLeads().then(({ data }) => setRows(data)); }, []);
  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-leads">
      <Header title="LinkedIn leads" subtitle="Public audit captures from the landing page." />
      <Table
        headers={["LinkedIn URL", "Email", "Score", "Source", "Date"]}
        rows={rows.map((l) => [
          <a key="url" className="text-[#0a66c2] hover:underline inline-flex items-center gap-1" href={l.url} target="_blank" rel="noreferrer">
            <span className="truncate max-w-[260px] inline-block align-middle">{l.url}</span>
            <ExternalLink size={12} />
          </a>,
          l.email || "—",
          <span key="score" className="font-mono">{l.result?.score ?? "—"}</span>,
          <span key="src" className="text-xs font-mono uppercase">{l.fetch_source || "—"}</span>,
          l.created_at ? new Date(l.created_at).toLocaleDateString() : "—",
        ])}
      />
    </div>
  );
}

/* ---------------- Contacts ---------------- */
export function AdminContacts() {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminContacts().then(({ data }) => setRows(data)); }, []);
  if (rows === null) return <SkeletonGrid />;

  return (
    <div data-testid="admin-contacts">
      <Header title="Contact forms" subtitle="Sales / partnership inquiries." />
      {rows.length === 0 ? <Empty msg="No submissions yet." /> : (
        <div className="mt-8 space-y-4">
          {rows.map((c) => (
            <div key={c.id} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-5">
              <div className="flex justify-between gap-4">
                <div>
                  <div className="font-medium">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                  <div className="text-xs text-stone-400">{c.email}</div>
                </div>
                <div className="text-xs text-stone-500">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                </div>
              </div>
              <p className="mt-3 text-sm text-stone-300 leading-relaxed">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Bits ---------------- */
function Header({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-6 flex-wrap">
      <div>
        <h1 className="font-display text-4xl">{title}</h1>
        {subtitle && <p className="mt-2 text-stone-400">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-32 rounded-2xl border border-stone-800 bg-[#1c1917] animate-pulse" />
      ))}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="p-12 text-center text-stone-400 rounded-2xl border border-stone-800 bg-[#1c1917]">
      {msg}
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-900/60 text-xs font-mono uppercase tracking-widest text-stone-400">
            <tr>{headers.map((h) => <Th key={h}>{h}</Th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-stone-800/80 hover:bg-stone-900/40">
                {row.map((c, j) => <Td key={j}>{c}</Td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-5 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-5 py-3 align-middle">{children}</td>;
const Kv = ({ k, v }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{k}</div>
    <div className="text-sm text-stone-200">{v || "—"}</div>
  </div>
);

const csv = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
